import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Tooltip,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import Seo from "./Seo";

/**
 * Shared shell for the prose pages (About, Security, Privacy, Terms).
 *
 * These four pages are structurally identical (badge, heading, standfirst, then a run of headed
 * sections), and differ only in words. Writing that scaffolding out four times would mean four places
 * to fix the next time a heading level or a mobile gutter is wrong, so the shape lives here and the
 * pages are reduced to content.
 *
 * Sections are data rather than JSX so the text stays readable as text, which matters most for the
 * legal pages where the wording is the thing under review.
 */

/**
 * One section. `heading` renders as an h2 so the page keeps a single h1 and a flat, crawlable
 * outline: answer engines lean on heading structure to decide which fragment answers a question.
 */
function Section({ heading, body = [], bullets = [], id }) {
  return (
    <Box component="section" sx={{ mt: 4 }} id={id}>
      <Typography
        variant="h5"
        component="h2"
        sx={{ fontWeight: "bold", mb: 1.5 }}
      >
        {heading}
      </Typography>
      {body.map((paragraph, i) => (
        <Typography key={i} sx={{ mb: 1.5, lineHeight: 1.7 }}>
          {paragraph}
        </Typography>
      ))}
      {bullets.length > 0 && (
        <Box component="ul" sx={{ pl: 3, m: 0, mb: 1.5 }}>
          {bullets.map((bullet, i) => (
            <Typography component="li" key={i} sx={{ mb: 1, lineHeight: 1.7 }}>
              {bullet}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function ContentPage({
  route,
  heading,
  badge,
  badgeLabel,
  standfirst,
  effectiveDate,
  sections = [],
  jsonLd = null,
  children,
}) {
  return (
    <Box
      sx={{ flexDirection: "column", backgroundColor: "background.default" }}
    >
      <Seo route={route} jsonLd={jsonLd} />
      {/* `md` rather than the default `lg`: long-form text at full desktop width runs past the ~75
          characters per line that stays comfortable to read. */}
      <Container maxWidth="md" sx={{ pb: 6 }}>
        <Stack
          // On a narrow phone the badge and a multi-word heading side by side left the heading a
          // column barely wide enough for one word, so it stacks below ~600px instead.
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          pt={3}
        >
          <Tooltip title={badgeLabel} arrow placement="top">
            <span>
              <img
                src={`badges/${badge}`}
                alt={badgeLabel}
                height={64}
                width={64}
              />
            </span>
          </Tooltip>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: "bold", pt: { xs: 0, sm: 1 } }}
          >
            {heading}
          </Typography>
        </Stack>

        {standfirst && (
          <Typography
            sx={{ mt: 2, fontSize: "1.125rem", lineHeight: 1.7 }}
            color="text.secondary"
          >
            {standfirst}
          </Typography>
        )}

        {effectiveDate && (
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Last updated {effectiveDate}. Questions about this page?{" "}
            <Link component={RouterLink} to="/contact">
              Get in touch
            </Link>
            .
          </Typography>
        )}

        {sections.map((section) => (
          <Section key={section.heading} {...section} />
        ))}

        {children}
      </Container>
    </Box>
  );
}
